<template>
  <div :class="$style.splitterPanel" :style="isTransitioning ? null : panelStyle" :data-panel-id="id">
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, watch } from "vue";

import type { SplitterContext } from "./splitterTypes";

const props = withDefaults(
  defineProps<{
    id: string;
    size?: number;
    minSize?: number;
    maxSize?: number;
    visible?: boolean;
    autofill?: boolean;
    isOverlay?: boolean;
    order?: number;
    isTransitioning?: boolean;
    collapsible?: boolean;
  }>(),
  {
    size: undefined,
    minSize: undefined,
    maxSize: undefined,
    visible: true,
    autofill: false,
    isOverlay: false,
    order: undefined,
    isTransitioning: false,
    collapsible: false,
  },
);

const splitterContext = inject<SplitterContext>("splitterContext");

const panelStyle = computed(() => ({
  overflow: "hidden",
  display: props.visible ? "block" : "none",
}));

onMounted(() => {
  if (splitterContext) {
    splitterContext.registerPanel({
      id: props.id,
      size: props.size,
      minSize: props.minSize,
      maxSize: props.maxSize,
      visible: props.visible,
      isOverlay: props.isOverlay,
      order: props.order,
      isTransitioning: props.isTransitioning,
      collapsible: props.collapsible,
    });
  }
});

onUnmounted(() => {
  if (splitterContext) {
    splitterContext.unregisterPanel(props.id);
  }
});

watch(
  () => props.visible,
  (newVisible) => {
    if (splitterContext) {
      splitterContext.registerPanel({
        id: props.id,
        size: props.size,
        minSize: props.minSize,
        maxSize: props.maxSize,
        visible: newVisible,
        isOverlay: props.isOverlay,
        order: props.order,
        isTransitioning: props.isTransitioning,
        collapsible: props.collapsible,
      });
    }
  },
);

watch(
  [
    () => props.size,
    () => props.minSize,
    () => props.maxSize,
    () => props.isOverlay,
    () => props.isTransitioning,
    () => props.collapsible,
  ],
  () => {
    if (splitterContext) {
      splitterContext.registerPanel({
        id: props.id,
        size: props.size,
        minSize: props.minSize,
        maxSize: props.maxSize,
        visible: props.visible,
        isOverlay: props.isOverlay,
        order: props.order,
        isTransitioning: props.isTransitioning,
        collapsible: props.collapsible,
      });
    }
  },
);
</script>

<style lang="scss" module>
.splitterPanel {
  position: relative;
  height: 100%;
}
</style>
